import React, { useEffect, useId, useState } from "react";
import { iconUrl, iconFallbackUrl, swapToFallback } from "./championAssets";
import { getInteractionsIndex } from "./proApi";

/** Percentile 0-100 -> readable color (red -> amber -> green). */
export function pctColor(v: number | null | undefined): string {
  if (v == null) return "#676e80";
  const hue = Math.round(Math.max(0, Math.min(100, v)) * 1.2); // 0..120
  return `hsl(${hue} 62% 52%)`;
}

export function Pct({ value }: { value: number | null | undefined }) {
  return (
    <span className="pro__pct" style={{ color: pctColor(value) }}>
      {value == null ? "—" : value}
    </span>
  );
}

export function TierChip({ tier }: { tier: string }) {
  return (
    <span className={`pro__tier-chip pro__tier-chip--${tier}`}>{tier}</span>
  );
}

export function ChampIcon({ name }: { name: string }) {
  const src = iconUrl(name);
  const fallback = iconFallbackUrl(name);
  if (!src) return null;
  return (
    <img
      src={src}
      alt=""
      loading="lazy"
      onError={(e) => swapToFallback(e, fallback)}
    />
  );
}

export function Champ({
  name,
  role,
  icon = true,
}: {
  name: string;
  role?: string | null;
  icon?: boolean;
}) {
  return (
    <span className="pro__champ">
      {icon && <ChampIcon name={name} />}
      {name}
      {role && <span className="pro__role">{role}</span>}
    </span>
  );
}

/** Champions + patches from the backend, fetched once per mount. */
export function useProIndex() {
  const [champions, setChampions] = useState<string[]>([]);
  const [patches, setPatches] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    getInteractionsIndex()
      .then((d) => {
        if (cancelled) return;
        setChampions(d.champions || []);
        setPatches(d.patches || []);
      })
      .catch((e) => {
        if (!cancelled) setError(e.message || "Failed to load index");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return { champions, patches, error };
}

export function PatchSelect({
  patches,
  value,
  onChange,
}: {
  patches: string[];
  value: string;
  onChange: (v: string) => void;
}) {
  const id = useId();
  return (
    <label className="pro__field" htmlFor={id}>
      Dataset
      <select id={id} value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">Current (all matches)</option>
        {patches.map((p) => (
          <option key={p} value={p}>
            patch {p}
          </option>
        ))}
      </select>
    </label>
  );
}

export function RoleSelect({
  label,
  value,
  onChange,
  allLabel = "All roles",
}: {
  label?: string;
  value: string;
  onChange: (v: string) => void;
  allLabel?: string;
}) {
  const id = useId();
  return (
    <label className="pro__field" htmlFor={id}>
      {label}
      <select id={id} value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">{allLabel}</option>
        {["Top", "Jungle", "Mid", "Bot", "Sup"].map((r) => (
          <option key={r} value={r}>
            {r}
          </option>
        ))}
      </select>
    </label>
  );
}

export function ChampionDatalist({
  id,
  champions,
}: {
  id: string;
  champions: string[];
}) {
  return (
    <datalist id={id}>
      {champions.map((c) => (
        <option key={c} value={c} />
      ))}
    </datalist>
  );
}
